import './style.scss';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import ColorCodedMultiCascader from 'app/components/ColorCodedMultiCascader';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, Divider, Dropdown, Form, Modal } from 'semantic-ui-react';
import { MultiCascader } from 'rsuite';
import useFiltersAssociationByProfileWithDefaults from 'app/hooks/useFiltersAssociationByProfileWithDefaults';
import useProcessingFiltersListWithDefaults from 'app/hooks/useProcessingFiltersListWithDefaults';
import {
  ANATOMIC_REGION_CASCADER_TREE,
  getAnatomicRegionFromString,
  localizeCascaderTreeData,
} from 'app/utils/xrayRegions';
import SpeciesListDropdown from 'app/components/Dropdown/SpeciesList';
import injectResetable, { ResetableComponentProps } from 'app/components/injectResetable';
import { ProcessingFiltersAssociationBySpecieAndRegion } from 'app/utils/PROCESSING_OPTIONS';
import _ from 'lodash';
import { setFiltersAssociationByProfile } from 'app/redux/processing/reducer';
import ConfirmWithIntl from 'app/components/ConfirmWithIntl';
import { useDispatch } from 'react-redux';
import TechnicianPasswordValidationContext from 'app/providers/TechnicianPasswordValidationProvider/context';
import { SpeciesType } from 'app/constants/species';

type Props = {};

const getFilterAssociationForRegion = (
  filtersAssociationByProfile: ProcessingFiltersAssociationBySpecieAndRegion | undefined,
  specie: SpeciesType | 'all',
  anatomicRegion: string
) => {
  const { mainRegion, subRegion } = getAnatomicRegionFromString(`${specie} ${anatomicRegion}`);
  return (
    filtersAssociationByProfile[specie]?.[`${mainRegion} ${subRegion}`] ??
    filtersAssociationByProfile[specie]?.[mainRegion] ??
    filtersAssociationByProfile[specie]?.default ??
    filtersAssociationByProfile.all?.[`${mainRegion} ${subRegion}`] ??
    filtersAssociationByProfile.all?.[mainRegion] ??
    filtersAssociationByProfile.all?.default
  );
};

const PostProcessingConfiguration = injectResetable(function ({
  reset,
}: Props & ResetableComponentProps) {
  const intl = useIntl();
  const dispatch = useDispatch();
  const filtersAssociationByProfile = useFiltersAssociationByProfileWithDefaults();
  const filtersList = useProcessingFiltersListWithDefaults();

  const requestValidation = useContext(TechnicianPasswordValidationContext);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDataChangeConfirmOpen, setIsDataChangeConfirmOpen] = useState(false);
  const [currentFiltersAssociationByProfile, setCurrentFiltersAssociationByProfile] = useState(
    filtersAssociationByProfile
  );
  const [anatomicRegions, setAnatomicRegions] = React.useState([]);
  const [specie, setSpecie] = React.useState<SpeciesType | 'all'>('all');

  const filtersOptions = useMemo(
    () =>
      filtersList.map((filter) => ({
        key: filter.id,
        text: filter.name,
        value: filter.id,
      })),
    [filtersList]
  );
  const localizedData = useMemo(
    () => localizeCascaderTreeData(ANATOMIC_REGION_CASCADER_TREE, intl),
    [intl]
  );
  const currentAssociatedFilterIds = useMemo(() => {
    return _.uniqBy(
      anatomicRegions?.map((region) =>
        getFilterAssociationForRegion(currentFiltersAssociationByProfile, specie, region)
      ),
      'id'
    );
  }, [anatomicRegions, currentFiltersAssociationByProfile, specie]);
  const displayedFiltersAssociationByProfile = useMemo(
    () =>
      _.mapValues(
        _.merge(
          {},
          currentFiltersAssociationByProfile.all,
          currentFiltersAssociationByProfile[specie]
        ),
        (value) => {
          return { name: filtersList.find((filter) => filter.id === value)?.name || '', value };
        }
      ),
    [currentFiltersAssociationByProfile, specie]
  );

  const requestModalClose = () => {
    if (!_.isEqual(currentFiltersAssociationByProfile, filtersAssociationByProfile)) {
      setIsDataChangeConfirmOpen(true);
    } else {
      setIsModalOpen(false);
    }
  };
  const handleExitDataChangeWithoutSave = () => {
    setIsDataChangeConfirmOpen(false);
    setCurrentFiltersAssociationByProfile(filtersAssociationByProfile);
    setIsModalOpen(false);
  };
  const saveFiltersAssociationByProfile = () => {
    dispatch(setFiltersAssociationByProfile(currentFiltersAssociationByProfile));
    setIsModalOpen(false);
  };
  const handleAnatomicRegionChange: React.ComponentProps<typeof MultiCascader>['onChange'] = (
    values: string[],
    event
  ) => {
    setAnatomicRegions(values.length === 0 ? undefined : values);
  };
  const onFilterChange: React.ComponentProps<typeof Dropdown>['onChange'] = (e, { value }) => {
    if (!anatomicRegions || anatomicRegions.length === 0 || !specie) return;

    setCurrentFiltersAssociationByProfile((prevState) => {
      return _.merge({}, prevState, {
        [specie]: _.fromPairs(anatomicRegions?.map((region) => [region, value])),
      });
    });
  };

  const openModal = () =>
    requestValidation()
      .then(() => setIsModalOpen(true))
      .catch(() => {});

  return (
    <>
      <Modal
        open={isModalOpen}
        onClose={reset}
        onOpen={openModal}
        trigger={
          <Button>
            <FormattedMessage id="general.configure" />
          </Button>
        }
        closeOnEscape={false}
        closeOnTriggerBlur={false}
        closeOnDocumentClick={false}
        closeOnDimmerClick={false}
        className="post-processing-configuration-modal"
      >
        <Modal.Header>
          <FormattedMessage id="account.configuration.processing.title" />
        </Modal.Header>
        <Modal.Content>
          <p>
            <FormattedMessage id="account.configuration.processing.explain" />
          </p>
          <div>
            <div className="specie-and-region-selector">
              <div className="field">
                <label>
                  <FormattedMessage id="account.configuration.processing.specie" />
                </label>
                <SpeciesListDropdown
                  value={specie}
                  placeholderIntlId="account.configuration.processing.specie"
                  onChange={setSpecie}
                  showAll
                />
              </div>
              <div className="field">
                <label>
                  <FormattedMessage id="account.configuration.processing.anatomic_region" />
                </label>
                <ColorCodedMultiCascader
                  placeholder={intl.formatMessage({
                    id: 'account.configuration.processing.anatomic_region',
                  })}
                  columnHeight={-1}
                  columnWidth={-1}
                  searchable={false}
                  data={localizedData}
                  value={anatomicRegions}
                  onChange={handleAnatomicRegionChange}
                  popupStyle={{ zIndex: 1001, textTransform: 'capitalize' }}
                  valuesByKey={displayedFiltersAssociationByProfile}
                  isChildKey={(valueKey, key) =>
                    (key.startsWith(valueKey) && key !== valueKey) || valueKey === 'default'
                  }
                  validityByKey={undefined}
                  invalidValueIntlKey="xray_area.constant.validation_errors.invalid"
                  invalidChildValueIntlKey="xray_area.constant.validation_errors.child_invalid"
                  preventOverflow
                  className="region-selector"
                  popupClassName="post-processing-region-selector-popup"
                />
              </div>
            </div>
            <Divider horizontal>
              <FormattedMessage id="account.configuration.processing.association" />
            </Divider>
            <div className="filter-association-selector">
              <Dropdown
                options={filtersOptions}
                onChange={onFilterChange}
                value={
                  currentAssociatedFilterIds.length === 1
                    ? currentAssociatedFilterIds[0]
                    : undefined
                }
                placeholder={intl.formatMessage({
                  id: 'account.configuration.processing.filter.placeholder',
                })}
                selection
                disabled={!anatomicRegions || anatomicRegions.length === 0 || !specie}
              />
            </div>
          </div>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={saveFiltersAssociationByProfile} positive>
            <FormattedMessage id="general.save" />
          </Button>
          <Button secondary onClick={requestModalClose}>
            <FormattedMessage id="general.close" />
          </Button>
        </Modal.Actions>
      </Modal>
      <ConfirmWithIntl
        onConfirm={handleExitDataChangeWithoutSave}
        onCancel={() => setIsDataChangeConfirmOpen(false)}
        contentId="account.configuration.processing.exit_without_save"
        open={isDataChangeConfirmOpen}
      />
    </>
  );
});

export default PostProcessingConfiguration;
